
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { tipsApi } from "@/services/api/tips";
import { createWinner, selectPrize } from "@/services/api/winners";
import { useQueryClient } from "@tanstack/react-query";

interface UseVotingProps {
  tipId: number;
  currentVotes: number;
  onVote: () => void;
  onPrizeWon: (prize: { name: string; description: string }) => void;
}

export function useVoting({ tipId, currentVotes, onVote, onPrizeWon }: UseVotingProps) {
  const [isVoted, setIsVoted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const COOLDOWN_PERIOD = 10000; // 10 seconds in milliseconds

  // Check local storage for vote timestamp on mount
  useEffect(() => {
    const lastVoteTime = localStorage.getItem(`lastVoted_${tipId}`);
    if (lastVoteTime) {
      const timeSinceLastVote = Date.now() - parseInt(lastVoteTime);
      if (timeSinceLastVote < COOLDOWN_PERIOD) {
        setIsVoted(true);
        // Set a timeout to enable voting again after the remaining cooldown time
        const remainingTime = COOLDOWN_PERIOD - timeSinceLastVote;
        setTimeout(() => {
          setIsVoted(false);
          localStorage.removeItem(`lastVoted_${tipId}`);
        }, remainingTime);
      } else {
        // Cooldown period has passed, clear the timestamp
        localStorage.removeItem(`lastVoted_${tipId}`);
      }
    }
  }, [tipId]);

  const showToast = (type: 'success' | 'error', title: string, description: string) => {
    toast({
      title: title,
      description: description,
      variant: type === 'success' ? 'default' : 'destructive',
      style: {
        padding: '20px', 
        fontSize: '18px', 
        maxWidth: '600px', 
        zIndex: 9999, 
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        display:'none'
      }
    });
  };

  const handleVote = async () => {
    if (isLoading || isVoted) {
      console.log('Vote blocked - loading or in cooldown:', { isLoading, isVoted });
      const lastVoteTime = localStorage.getItem(`lastVoted_${tipId}`);
      if (lastVoteTime) {
        const remainingTime = Math.ceil((COOLDOWN_PERIOD - (Date.now() - parseInt(lastVoteTime))) / 1000);
        showToast('error', "Please wait", `You can vote again in ${remainingTime} seconds`);
      }
      return;
    }

    try {
      setIsLoading(true);
      console.log('Starting vote process for tip:', tipId);
      
      // Update votes using atomic increment
      const tipData = await tipsApi.updateTipVotes(tipId);
      
      if (!tipData) {
        throw new Error('Failed to update vote count');
      }

      const selectedPrize = await selectPrize(tipId);
      
      if (selectedPrize) {
        const winnerData = {
          prize_id: selectedPrize.id,
          tip_id: tipId,
          vote_content: tipData.content,
          source: 'vote',
          device_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language
          }
        };

        try {
          const { prize } = await createWinner(winnerData);
          if (prize) {
            onPrizeWon(prize);
          }
        } catch (winnerError) {
          console.error('Error creating winner entry:', winnerError);
          // Continue execution even if winner creation fails
        }
      }

      // Set vote timestamp in localStorage
      localStorage.setItem(`lastVoted_${tipId}`, Date.now().toString());
      setIsVoted(true);
      
      // Set timeout to enable voting again after cooldown
      setTimeout(() => {
        setIsVoted(false);
        localStorage.removeItem(`lastVoted_${tipId}`);
      }, COOLDOWN_PERIOD);
      
      // Notify parent component
      onVote();

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ 
        queryKey: ['approved-tips'],
        exact: true 
      });

      showToast('success', "Thank you for voting!", "Check if you've won a prize!");

    } catch (error) {
      console.error('Error processing vote:', error);
      showToast('error', "Error", "Something went wrong while processing your vote. Please try again.");
      // Reset vote state in case of error
      setIsVoted(false);
      localStorage.removeItem(`lastVoted_${tipId}`);
    } finally {
      setIsLoading(false);
      console.log('Vote process completed, states:', { isVoted, isLoading });
    }
  };

  return {
    isVoted,
    setIsVoted,
    isLoading,
    handleVote
  };
}
